"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryItems = [{
  id: "proj-1",
  title: "ICT Centre",
  location: "Ajumako Baa, Central Region of Ghana",
  image: "https://touching-owl.10web.cloud/wp-content/uploads/2026/03/17.jpeg?v=zf73u778qpe",
  description: "We completed an Information and Communication Technology (ICT) Centre at Ajumako Baa in the Central Region of Ghana. This project improves access to technology, digital learning, and practical ICT education for children and young people in the community.",
  category: "ICT Development",
  status: "Completed"
}, {
  id: "proj-2",
  title: "School Transport Van",
  location: "Central Region of Ghana",
  image: "https://touching-owl.10web.cloud/wp-content/uploads/2026/03/16.jpeg?v=1bear5wme0w",
  description: "We provided a small van to transport school children safely to and from school. This project helps improve access to education by making daily travel easier, safer, and more reliable for children and families.",
  category: "School Support",
  status: "Completed"
}, {
  id: "proj-3",
  title: "Planned Community Project",
  location: "Ghana",
  image: "https://touching-owl.10web.cloud/wp-content/uploads/2026/03/18.jpeg?v=gx9ver8p8kc",
  description: "This is one of our upcoming projects and is currently in preparation. It reflects our continued commitment to supporting communities through education, infrastructure, and opportunity. More details will be shared as implementation begins.",
  category: "Upcoming Project",
  status: "Starting Soon"
}];
function getStatusClasses(status: string) {
  switch (status) {
    case "Completed":
      return "bg-green-600 text-white";
    case "In Progress":
      return "bg-amber-500 text-white";
    case "Starting Soon":
      return "bg-blue-600 text-white";
    default:
      return "bg-secondary text-secondary-foreground";
  }
}
export default function TheWorkInMotion() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: galleryRef,
    isVisible: isGalleryVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="104374" id="educational-initiatives" className="relative overflow-hidden bg-background py-10 sm:py-14 md:py-16 lg:py-20">
      <div className="pointer-events-none absolute top-0 left-1/2 -z-10 h-[240px] w-[90%] max-w-[800px] -translate-x-1/2 rounded-full bg-primary/5 blur-3xl sm:h-[300px] md:h-[400px]" />

      <div className="container mx-auto px-4 sm:px-6">
        <div ref={headerRef} className={`mx-auto mb-10 max-w-3xl text-center transition-all duration-1000 ease-out sm:mb-12 md:mb-16 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="mb-4 text-3xl font-serif leading-tight tracking-tight text-primary sm:mb-5 sm:text-4xl md:mb-6 md:text-5xl">
            Our Impact in Ghana
          </h2>

          <p className="font-sans text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
            We are a new organisation. So far, we have completed two important
            projects in Ghana: an Information and Communication Technology (ICT)
            Centre at Ajumako Baa in the Central Region of Ghana, and a small
            van to transport children to and from school. We are also preparing
            for additional projects that will further expand our impact in
            education and community development.
          </p>
        </div>

        <div ref={galleryRef} className={`grid grid-cols-1 gap-6 transition-all duration-1000 delay-200 ease-out md:grid-cols-2 xl:grid-cols-3 lg:gap-8 ${isGalleryVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          {galleryItems.map((item, i) => <div key={item.id} data-index={i}>
              <Dialog>
                <DialogTrigger asChild>
                  <div className="group relative cursor-pointer overflow-hidden rounded-2xl bg-accent/30 shadow-sm transition-all duration-500 hover:shadow-xl">
                    <img src={item.image} alt={item.title} className="h-[260px] w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-[320px] md:h-[360px] lg:h-[420px]" loading="lazy" />

                    <div className="absolute left-4 top-4 z-10">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold tracking-wide ${getStatusClasses(item.status)}`}>
                        {item.status}
                      </span>
                    </div>

                    <div className="absolute inset-0 flex flex-col justify-end bg-primary/85 p-4 opacity-100 transition-opacity duration-300 sm:p-6 md:p-8 sm:opacity-0 sm:group-hover:opacity-100">
                      <div className="translate-y-0 transform transition-transform duration-300 sm:translate-y-4 sm:group-hover:translate-y-0">
                        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/10 backdrop-blur-sm sm:mb-4">
                          <ZoomIn className="h-5 w-5 text-primary-foreground" />
                        </div>

                        <h3 className="mb-2 font-serif text-xl leading-tight text-primary-foreground sm:text-2xl md:text-3xl">
                          {item.title}
                        </h3>

                        <p className="flex items-center gap-2 font-sans text-sm font-medium text-primary-foreground/90 sm:text-base">
                          <MapPin className="h-4 w-4 shrink-0" />
                          <span>{item.location}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </DialogTrigger>

                <DialogContent className="max-w-[95vw] gap-0 overflow-hidden border-none bg-transparent p-0 shadow-2xl sm:max-w-4xl sm:rounded-2xl md:max-w-5xl">
                  <div className="flex max-h-[90vh] w-full flex-col bg-card md:flex-row">
                    <div className="flex items-center justify-center bg-primary/95 p-4 sm:p-5 md:w-3/5 md:p-8">
                      <img src={item.image} alt={item.title} className="w-full max-h-[35vh] rounded-lg object-contain shadow-2xl sm:max-h-[40vh] md:max-h-[80vh]" />
                    </div>

                    <div className="flex flex-col overflow-y-auto bg-card p-5 sm:p-6 md:w-2/5 md:p-10">
                      <DialogHeader className="mb-5 space-y-3 text-left sm:mb-6 sm:space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="inline-flex w-fit items-center rounded-full border border-transparent bg-secondary px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-secondary-foreground sm:text-xs">
                            {item.category}
                          </div>

                          <div className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider sm:text-xs ${getStatusClasses(item.status)}`}>
                            {item.status}
                          </div>
                        </div>

                        <DialogTitle className="font-serif text-2xl leading-tight text-primary sm:text-3xl md:text-4xl">
                          {item.title}
                        </DialogTitle>

                        <DialogDescription className="flex items-center gap-2 font-sans text-sm text-muted-foreground sm:text-base">
                          <MapPin className="h-4 w-4 shrink-0 text-secondary" />
                          <span>{item.location}</span>
                        </DialogDescription>
                      </DialogHeader>

                      <div className="font-sans text-sm leading-7 text-foreground/80 sm:text-base">
                        <p>{item.description}</p>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}